import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Gift, Loader2, UserCheck, Clock, XCircle, Mail, Ticket, Copy, Plus, School,
} from "lucide-react";
import { instructorGift, listInstructorGifts, instructorRevoke, createCodes, listCodes } from "@/lib/api";
import { InstructorPage } from "@/pages/InstructorPage";
import { toast } from "sonner";

const SECTIONS = [
  { id: "classes", label: "Classes", icon: School },
  { id: "gift", label: "Gift Pro", icon: Gift },
  { id: "codes", label: "Codes", icon: Ticket },
];

export const InstructorPanel = ({ onPreviewLecture, onGoCreate }) => {
  const [section, setSection] = useState("classes");
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Instructor</span>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold">Class control center</h1>
        <p className="mt-1 text-muted-foreground">Run your classes, gift Pro, and hand out redemption codes — all in one place.</p>
      </div>

      <div className="flex gap-1 rounded-full border border-border p-1 w-fit mb-8">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            data-testid={`instr-section-${s.id}`}
            onClick={() => setSection(s.id)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${section === s.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            <s.icon className="h-4 w-4" /> {s.label}
          </button>
        ))}
      </div>

      {section === "classes" && <InstructorPage onPreviewLecture={onPreviewLecture} onGoCreate={onGoCreate} />}
      {section === "gift" && <GiftSection />}
      {section === "codes" && <CodesSection />}
    </div>
  );
};

const GiftSection = () => {
  const [email, setEmail] = useState("");
  const [gifts, setGifts] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => listInstructorGifts().then(setGifts).catch(() => setGifts([]));
  useEffect(() => { refresh(); }, []);

  const grant = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const res = await instructorGift(email.trim());
      toast.success(res.status === "redeemed" ? `Pro granted to ${res.email}` : `Pending — unlocks when ${res.email} signs up`);
      setEmail(""); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not grant"); }
    finally { setBusy(false); }
  };

  const revoke = async (em) => { await instructorRevoke(em).catch(() => {}); toast.success(`Revoked ${em}`); refresh(); };

  return (
    <>
      <form onSubmit={grant} className="rounded-2xl border border-border bg-card p-6 shadow-sm mb-4">
        <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Gift one student</label>
        <div className="mt-2 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input data-testid="instructor-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="student@school.edu"
              className="w-full rounded-xl border border-input bg-background pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
          </div>
          <button data-testid="instructor-grant-btn" type="submit" disabled={busy}
            className="flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground px-6 py-3 font-semibold disabled:opacity-50 hover:opacity-90 transition">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />} Grant Pro
          </button>
        </div>
      </form>

      <h2 className="font-serif text-2xl font-semibold mb-4">Granted access</h2>
      {gifts === null ? <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
      : gifts.length === 0 ? <p className="text-muted-foreground text-sm py-8 text-center">No students gifted yet.</p>
      : (
        <div className="space-y-2">
          {gifts.map((g, i) => (
            <div key={i} data-testid="instructor-gift-row" className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className={`grid place-items-center h-8 w-8 rounded-full shrink-0 ${g.status === "revoked" ? "bg-destructive/10 text-destructive" : g.active ? "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))]" : "bg-secondary text-muted-foreground"}`}>
                  {g.status === "revoked" ? <XCircle className="h-4 w-4" /> : g.active ? <UserCheck className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{g.email}</p>
                  <p className="text-xs text-muted-foreground">{g.status === "revoked" ? "Revoked" : g.active ? "Pro active" : g.has_account ? "Account exists" : "Pending sign-up"}</p>
                </div>
              </div>
              {g.status !== "revoked" && (
                <button data-testid="instructor-revoke-btn" onClick={() => revoke(g.email)}
                  className="text-xs font-semibold rounded-full border border-border px-3 py-1.5 hover:text-destructive hover:border-destructive/50 transition">Revoke</button>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const CodesSection = () => {
  const [codes, setCodes] = useState(null);
  const [count, setCount] = useState(5);
  const [busy, setBusy] = useState(false);
  const refresh = () => listCodes().then(setCodes).catch(() => setCodes([]));
  useEffect(() => { refresh(); }, []);

  const generate = async () => {
    setBusy(true);
    try { await createCodes(count); refresh(); toast.success(`${count} code(s) generated`); }
    catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401) toast.error("Session expired — please sign in again, then retry.");
      else if (status === 403) toast.error(detail || "This account doesn't have instructor access. Try the admin Dashboard → Beta lifetime codes instead.");
      else toast.error(detail || "Could not generate codes — please retry in a moment.");
    }
    finally { setBusy(false); }
  };
  const copy = (c) => { navigator.clipboard?.writeText(c); toast.success("Copied " + c); };

  return (
    <>
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm mb-8 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">How many codes?</label>
          <input data-testid="code-count" type="number" min={1} max={100} value={count} onChange={(e) => setCount(Number(e.target.value))}
            className="mt-2 w-28 rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
        </div>
        <button data-testid="generate-codes-btn" onClick={generate} disabled={busy}
          className="flex items-center gap-2 rounded-xl bg-accent text-accent-foreground px-6 py-3 font-semibold disabled:opacity-50 hover:opacity-90 transition">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Generate codes
        </button>
        <p className="text-sm text-muted-foreground">Share a code — a student redeems it in Upgrade → "Have a code?" to unlock Pro.</p>
      </div>

      {codes === null ? <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
      : codes.length === 0 ? <p className="text-muted-foreground text-sm py-8 text-center">No codes yet.</p>
      : (
        <div className="grid sm:grid-cols-2 gap-2">
          {codes.map((c) => (
            <div key={c.code} data-testid="code-row" className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
              <div>
                <p className="font-mono font-bold">{c.code}</p>
                <p className="text-xs text-muted-foreground">{c.status === "redeemed" ? `Used by ${c.redeemed_email}` : "Available"}</p>
              </div>
              {c.status === "active" && (
                <div className="flex gap-1">
                  <button onClick={() => copy(c.code)} title="Copy code" className="grid place-items-center h-8 w-8 rounded-full border border-border hover:border-primary/50 transition"><Copy className="h-4 w-4" /></button>
                  <a
                    data-testid="email-code-btn"
                    href={`mailto:?subject=${encodeURIComponent("Your Lectura Pro code")}&body=${encodeURIComponent(`Here's your Lectura Pro code: ${c.code}\n\nTo redeem it:\n1. Go to ${window.location.origin} and create an account (or sign in).\n2. Open Upgrade and enter the code under "Have a code?" — or paste it on the sign-up screen.\n\nHappy studying!`)}`}
                    title="Email code"
                    className="grid place-items-center h-8 w-8 rounded-full border border-border hover:border-primary/50 transition"
                  >
                    <Mail className="h-4 w-4" />
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};
